import { apiRoutes } from "../types/api-routes";
import { baseUrl } from "../types/config";
// Mock helpers intentionally kept in repo for fallback/dev troubleshooting.
// import {
//   getMockDefaultSizingChartsResponse,
//   getMockLingerieSizingChartsResponse,
//   isSizingChartsMockEnabled,
//   mockSizingChartMutationResponse,
//   mockToggleFitResponse,
// } from "../mocks/sizingChartsMock";
import service from "./service";

/**
 * @typedef {Object} VendorSizingChartMeasurementRange
 * @property {number|null} min
 * @property {number|null} max
 *
 * @typedef {Object} VendorSizingChartSizeRow
 * @property {string} name
 * @property {VendorSizingChartMeasurementRange|null} [neck]
 * @property {VendorSizingChartMeasurementRange|null} [chest]
 * @property {VendorSizingChartMeasurementRange|null} [sleeve]
 * @property {VendorSizingChartMeasurementRange|null} [hip]
 * @property {VendorSizingChartMeasurementRange|null} [waist]
 * @property {VendorSizingChartMeasurementRange|null} [inseam]
 *
 * @typedef {Object} VendorSizingChartItemRequest
 * @property {string} [id]
 * @property {VendorSizingChartSizeRow[]} sizes
 *
 * @typedef {Object} VendorSizingChartRequest
 * @property {VendorSizingChartItemRequest[]} items
 * @property {string} chartName
 * @property {string} brandId
 * @property {string} unit
 * @property {string} gender
 * @property {string} clothingType
 */

export const uploadProducts = (data, signal) => {
  return service({
    url: `${baseUrl}/${apiRoutes.UPLOAD_PRODUCTS}`,
    data,
    method: "post",
    signal,
  });
};

export const getProductUploadInstances = (params) => {
  return service({
    url: `${baseUrl}/${apiRoutes.PRODUCTS_INSTANCES}`,
    params,
    method: "get",
  });
};

export const getProducts = (params) => {
  return service({
    url: `${baseUrl}/${apiRoutes.PRODUCTS}`,
    params,
    method: "get",
  });
};

export const createProduct = (data) => {
  return service({
    url: `${baseUrl}/${apiRoutes.PRODUCTS}`,
    data,
    method: "post",
  });
};

export const updateProduct = (data) => {
  return service({
    url: `${baseUrl}/${apiRoutes.PRODUCTS}`,
    data,
    method: "patch",
  });
};

export const getProductById = ({ id }) => {
  return service({
    url: `${baseUrl}/${apiRoutes.PRODUCTS}/${id}`,
    method: "get",
  });
};

export const deleteProduct = ({ id }) => {
  return service({
    url: `${baseUrl}/${apiRoutes.PRODUCTS}/${id}`,
    method: "delete",
  });
};

export const getDefaultSizingCharts = () => {
  // if (isSizingChartsMockEnabled()) {
  //   return Promise.resolve(getMockDefaultSizingChartsResponse());
  // }
  return service({
    url: `${baseUrl}/${apiRoutes.DEFAULT_CHART}`,
    method: "get",
  });
};

export const getLingerieSizingCharts = () => {
  // if (isSizingChartsMockEnabled()) {
  //   return Promise.resolve(getMockLingerieSizingChartsResponse());
  // }
  return service({
    url: `${baseUrl}/${apiRoutes.LINGERIE_CHART}`,
    method: "get",
  });
};

export const updateSizingChart = (data) => {
  // if (isSizingChartsMockEnabled()) {
  //   return mockSizingChartMutationResponse();
  // }
  return service({
    url: `${baseUrl}/${apiRoutes.UPDATE_SIZING_CHART}`,
    data,
    method: "patch",
  });
};

export const updateLingerieSizingChart = (data) => {
  // if (isSizingChartsMockEnabled()) {
  //   return mockSizingChartMutationResponse();
  // }
  return service({
    url: `${baseUrl}/${apiRoutes.UPDATE_LINGERIE_CHART}`,
    data,
    method: "patch",
  });
};

/**
 * @param {boolean} isFitEnabled Target enabled state for general (non-lingerie) fit recommendations.
 */
export const toggleUseFit = (isFitEnabled) => {
  // if (isSizingChartsMockEnabled()) {
  //   return mockToggleFitResponse();
  // }
  return service({
    url: `${baseUrl}/${apiRoutes.TOGGLE_FIT}`,
    method: "post",
    data: { isFitEnabled },
  });
};

export const toggleUseLingerieFit = () => {
  // if (isSizingChartsMockEnabled()) {
  //   return mockToggleFitResponse();
  // }
  return service({
    url: `${baseUrl}/${apiRoutes.TOGGLE_LINGERIE_FIT}`,
    method: "get",
  });
};

/* Vendor brands */
export const getVendorBrands = () =>
  service({
    url: `${baseUrl}/${apiRoutes.VENDOR_BRANDS}`,
    method: "get",
  });

export const createVendorBrand = (data) =>
  service({
    url: `${baseUrl}/${apiRoutes.VENDOR_BRAND}`,
    data,
    method: "post",
  });

export const updateVendorBrand = ({ id, data }) =>
  service({
    url: `${baseUrl}/${apiRoutes.VENDOR_BRAND}/${id}`,
    data,
    method: "patch",
  });

export const deleteVendorBrand = ({ id }) =>
  service({
    url: `${baseUrl}/${apiRoutes.VENDOR_BRAND}/${id}`,
    method: "delete",
  });

/* Vendor sizing charts */
export const getVendorSizingCharts = () =>
  service({
    url: `${baseUrl}/${apiRoutes.VENDOR_SIZING_CHARTS}`,
    method: "get",
  });

export const getVendorSizingChartById = ({ id }) =>
  service({
    url: `${baseUrl}/${apiRoutes.VENDOR_SIZING_CHART}/${id}`,
    method: "get",
  });

/** @param {VendorSizingChartRequest} data */
export const createVendorSizingChart = (data) =>
  service({
    url: `${baseUrl}/${apiRoutes.VENDOR_SIZING_CHART}`,
    data,
    method: "post",
  });

/** @param {{ id: string, data: VendorSizingChartRequest }} params */
export const patchVendorSizingChart = ({ id, data }) =>
  service({
    url: `${baseUrl}/${apiRoutes.VENDOR_SIZING_CHART}/${id}`,
    data,
    method: "patch",
  });

export const deleteVendorSizingChart = ({ id }) =>
  service({
    url: `${baseUrl}/${apiRoutes.VENDOR_SIZING_CHART}/${id}`,
    method: "delete",
  });
